import DashboardLayout from '@/components/DashboardLayout';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUpload } from '@/components/ui/image-upload';
import { ArrowLeft, CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

function parseCurrencyInput(raw: string): number {
  const digits = raw.replace(/\D/g, '');
  return parseInt(digits || '0', 10);
}

function formatCentsInput(cents: number): string {
  if (cents === 0) return '';
  return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function New() {
  const [form, setForm] = useState({
    title: '',
    description: '',
    event_type: 'trip',
    location: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    capacity: '',
    price_cents: 0,
    requires_payment: false,
    requires_approval: true,
  });
  const [banner, setBanner] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    if (!form.title || !form.start_date) {
      toast.error('Titulo e data de inicio sao obrigatorios');
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'capacity' && value === '') return;
      formData.append(`event[${key}]`, String(value));
    });
    if (banner) formData.append('event[banner]', banner);

    router.post('/events', formData, {
      forceFormData: true,
      onSuccess: () => setSubmitting(false),
      onError: () => setSubmitting(false),
    });
  };

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => router.visit('/events')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Novo Evento</h1>
            <p className="text-muted-foreground">Criar trip, torneio, workshop ou festa</p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Informacoes Basicas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titulo *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Digite o titulo" />
              </div>
              <div>
                <Label>Descricao</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={4} placeholder="Digite a descricao" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo *</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trip">Trip</SelectItem>
                      <SelectItem value="tournament">Torneio</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="party">Festa</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Local</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" placeholder="Digite o local" />
                </div>
              </div>
              <div>
                <Label>Banner</Label>
                <div className="mt-1">
                  <ImageUpload onImageSelect={setBanner} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Datas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data de Inicio *</Label>
                  <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Data de Fim</Label>
                  <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>Prazo de Inscricao</Label>
                <Input type="datetime-local" value={form.registration_deadline} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} className="mt-1" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Vagas e Pagamento</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Capacidade (deixe vazio para ilimitado)</Label>
                <Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1" placeholder="Ilimitado" />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">Aprovacao manual</p>
                  <p className="text-xs text-muted-foreground">Admin aprova cada inscricao</p>
                </div>
                <Switch checked={form.requires_approval} onCheckedChange={(v) => setForm({ ...form, requires_approval: v })} />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">Requer pagamento</p>
                  <p className="text-xs text-muted-foreground">Inscricao com cobranca</p>
                </div>
                <Switch checked={form.requires_payment} onCheckedChange={(v) => setForm({ ...form, requires_payment: v, price_cents: v ? form.price_cents : 0 })} />
              </div>
              {form.requires_payment && (
                <div>
                  <Label>Preco</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={formatCentsInput(form.price_cents)}
                      onChange={(e) => setForm({ ...form, price_cents: parseCurrencyInput(e.target.value) })}
                      className="pl-9"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => router.visit('/events')}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              <CalendarDays className="w-4 h-4 mr-1" />
              Criar Evento
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
